import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Heart, MessageCircle, Shield, AlertCircle, Flag, RefreshCw, FileText, Users } from 'lucide-react';
import { DinoLogo } from '@/components/DinoLogo';

export default function CommunityGuidelines() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-[var(--header-height)] z-40 bg-background/80 backdrop-blur-sm border-b">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <DinoLogo size={32} />
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <span className="text-5xl">🦖</span>
            </div>
            <h1 className="text-4xl font-heading font-bold mb-4 text-foreground">
              Dino Community Guidelines
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Welcome to <strong>Dino</strong> — the reading community that helps you share your story, stay accountable to your reading goals, and find your next favourite book.
            </p>
          </div>

          <Card className="mb-8 bg-butter/5 border-butter/20">
            <CardContent className="p-6">
              <p className="text-foreground mb-0">
                We're building a space that feels like your favourite corner of a bookshop: friendly, curious, and never judgmental. To keep it that way, please follow our community guidelines.
              </p>
            </CardContent>
          </Card>

          {/* Core Values */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="h-7 w-7 text-primary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">1. Our Core Values</h2>
            </div>
            <p className="text-foreground">
              Dino is built on three simple principles:
            </p>
            <ul className="space-y-3 text-foreground">
              <li className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0">📘</span>
                <span><strong>Be kind</strong> — disagree with ideas, not people.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0">📗</span>
                <span><strong>Be honest</strong> — your true opinions make Dino better.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0">📙</span>
                <span><strong>Be respectful</strong> — every author and reader deserves decency.</span>
              </li>
            </ul>
            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  You can be critical without being cruel. This is a space for thoughtful readers, not trolls.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Respectful Conduct */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="h-7 w-7 text-secondary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">2. Respectful Conduct</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-success">✅ Do</h3>
                <ul className="space-y-1 text-foreground">
                  <li>Encourage others and celebrate different reading journeys</li>
                  <li>Debate ideas — with empathy and curiosity</li>
                  <li>Keep conversations about books, not people</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2 text-destructive">❌ Don't</h3>
                <ul className="space-y-1 text-foreground">
                  <li>No hate speech, discrimination, bullying, or harassment</li>
                  <li>No trolling, spam, or baiting arguments</li>
                  <li>No offensive or harmful posts</li>
                </ul>
              </div>
            </div>

            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  If you wouldn't say it out loud in a friendly book club, don't post it here.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Reviews & Ratings */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="h-7 w-7 text-accent" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">3. Reviews & Ratings</h2>
            </div>
            <p className="text-foreground">
              We love honest, detailed reviews — they make Dino thrive.
            </p>
            <ul className="space-y-2 text-foreground">
              <li>Keep reviews focused on the <strong>book</strong>, not the author or another reader</li>
              <li>Avoid spoilers (or clearly mark them)</li>
              <li>Don't post fake or copy-pasted reviews</li>
              <li>Disclose if you received a free or advance copy</li>
            </ul>
            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  Transparency builds trust.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Content & Posting Rules */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-7 w-7 text-primary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">4. Content & Posting Rules</h2>
            </div>
            <p className="text-foreground">
              Dino is for readers, not advertisers.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-success">✅ Welcome</h3>
                <p className="text-foreground">Book talk, reading goals, quotes, shelfies, and discussions</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2 text-destructive">❌ Not allowed</h3>
                <ul className="space-y-1 text-foreground">
                  <li>No explicit, hateful, or violent content</li>
                  <li>No uploading copyrighted works (like full e-books)</li>
                  <li>No unrelated self-promotion or ads</li>
                </ul>
              </div>
            </div>

            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  Think: would this make another reader smile, think, or want to pick up a book?
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Privacy & Safety */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="h-7 w-7 text-secondary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">5. Privacy & Safety</h2>
            </div>
            <ul className="space-y-2 text-foreground">
              <li>Don't share private or identifying information (yours or anyone else's)</li>
              <li>Report anything that feels off — our team and moderation tools will step in</li>
              <li>Dino may remove posts or accounts that break these rules</li>
            </ul>
            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  Your safety and comfort come first.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Age & Privacy */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Users className="h-7 w-7 text-primary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">6. Age & Privacy</h2>
            </div>
            <p className="text-foreground">
              Dino is a space for readers — not kids' content — so we have a few simple rules to keep everyone safe:
            </p>

            <div className="space-y-6 mt-6">
              {/* Under 13 */}
              <Card className="border-destructive/20 bg-destructive/5">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">📚</span>
                    <div>
                      <h3 className="font-semibold mb-2 text-foreground">Under 13</h3>
                      <p className="text-sm text-foreground">
                        Sorry, you'll need to wait a bit before joining. Dino doesn't allow accounts for anyone under 13, in line with child data protection laws.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ages 13-15 */}
              <Card className="border-secondary/20 bg-secondary/5">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">🦖</span>
                    <div>
                      <h3 className="font-semibold mb-2 text-foreground">Ages 13–15</h3>
                      <p className="text-sm text-foreground mb-3">
                        You're welcome here! But your account will automatically be <strong>private by default</strong>.
                      </p>
                      <p className="text-sm text-foreground mb-2">That means:</p>
                      <ul className="text-sm text-foreground space-y-1 ml-4">
                        <li>• Only approved followers can see your profile and posts</li>
                        <li>• You won't appear in public search results</li>
                        <li>• You'll have limited access to social and messaging features</li>
                        <li>• Profile pictures are <strong>optional</strong> and checked for safety before appearing</li>
                      </ul>
                      <p className="text-sm text-foreground mt-3">
                        Once you turn 16, you can choose to make your account public — but you'll always stay in control.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 16 and over */}
              <Card className="border-success/20 bg-success/5">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">🧡</span>
                    <div>
                      <h3 className="font-semibold mb-2 text-foreground">16 and over</h3>
                      <p className="text-sm text-foreground">
                        You can choose whether your account is private or public, and set your own visibility preferences at any time.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  All profile photos uploaded to Dino are automatically checked for inappropriate content before appearing, so the whole community stays welcoming and safe.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Moderation & Enforcement */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-7 w-7 text-accent" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">7. Moderation & Enforcement</h2>
            </div>
            <p className="text-foreground">
              We take moderation seriously — with a mix of human review and Dino's smart filters.
            </p>
            <p className="text-foreground">
              Breaking the rules may lead to:
            </p>
            <ol className="space-y-2 text-foreground">
              <li>1️⃣ A gentle warning 🦖</li>
              <li>2️⃣ A temporary suspension 🕒</li>
              <li>3️⃣ Permanent removal 🚫</li>
            </ol>
            <Card className="mt-4 bg-muted/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-0">
                  Persistent bad behaviour = permanent ban.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Reporting & Feedback */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Flag className="h-7 w-7 text-primary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">8. Reporting & Feedback</h2>
            </div>
            <p className="text-foreground">
              See something that doesn't belong? <strong>Report it.</strong>
            </p>
            <p className="text-foreground">
              We review every report and appreciate your help keeping Dino positive and bookish.
            </p>
            <p className="text-foreground">
              Have feedback or ideas? We'd love to hear from you — drop us a message via <em>Settings → Feedback.</em>
            </p>
          </section>

          {/* Updates & Evolution */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <RefreshCw className="h-7 w-7 text-secondary" />
              <h2 className="text-2xl font-heading font-bold mb-0 text-foreground">9. Updates & Evolution</h2>
            </div>
            <p className="text-foreground">
              As Dino grows, these guidelines might evolve. We'll always let you know when we make updates — and why.
            </p>
          </section>

          {/* Closing */}
          <Card className="bg-gradient-to-br from-butter/10 to-avocado/10 border-butter/30">
            <CardContent className="p-8 text-center">
              <div className="text-4xl mb-4">💬</div>
              <h2 className="text-2xl font-heading font-bold mb-3 text-foreground">The Heart of Dino</h2>
              <p className="text-foreground text-lg mb-2">
                Dino is for readers who care — about books, about stories, and about each other.
              </p>
              <p className="text-foreground text-lg font-medium mb-0">
                Thank you for helping us keep it that way. 💛
              </p>
            </CardContent>
          </Card>

          {/* Back Button */}
          <div className="flex justify-center mt-8">
            <Button
              size="lg"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Go Back
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
